/**
 * JobMockAI — ReadinessTrendChart (Phase 4 Step 8)
 *
 * Line chart showing readiness score over time with milestone markers.
 */
import React from 'react';
import { Box, Card, CardContent, Paper, Skeleton, Typography } from '@mui/material';
import { TrendingUp } from '@mui/icons-material';

const ReadinessTrendChart = ({ history = [], milestones = [], loading }) => {
  if (loading) {
    return (
      <Card variant="outlined" data-testid="readiness-trend-loading">
        <CardContent>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rectangular" height={200} sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    );
  }

  if (!history.length) {
    return (
      <Card variant="outlined" data-testid="readiness-trend-empty">
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <TrendingUp sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Complete more interviews to see your readiness trend.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Simple SVG-based line chart (no heavy chart library dependency)
  const maxScore = 100;
  const width = 600;
  const height = 200;
  const padding = 30;
  const chartW = width - padding * 2;
  const chartH = height - padding * 2;

  const points = history.map((h, i) => ({
    x: padding + (i / Math.max(history.length - 1, 1)) * chartW,
    y: padding + chartH - ((h.score || h.overall_score || 0) / maxScore) * chartH,
    score: h.score || h.overall_score || 0,
    date: h.date || h.calculated_at || '',
  }));

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <Card variant="outlined" data-testid="readiness-trend">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TrendingUp color="primary" />
          <Typography variant="h6" fontWeight={700}>Readiness Trend</Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', maxHeight: 220 }} data-testid="trend-chart-svg">
            {/* Grid lines */}
            {[0, 25, 50, 75, 100].map((v) => {
              const y = padding + chartH - (v / maxScore) * chartH;
              return (
                <g key={v}>
                  <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#eee" strokeWidth="1" />
                  <text x={padding - 5} y={y + 4} textAnchor="end" fontSize="10" fill="#999">{v}</text>
                </g>
              );
            })}
            {/* Line */}
            <path d={linePath} fill="none" stroke="#0D47A1" strokeWidth="2.5" data-testid="trend-line" />
            {/* Dots */}
            {points.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="4" fill="#0D47A1" data-testid={`trend-point-${i}`}>
                <title>{`${p.date}: ${p.score}`}</title>
              </circle>
            ))}
            {/* Milestone markers */}
            {milestones.map((m, i) => {
              const idx = history.findIndex(h => (h.date || h.calculated_at) === m.date);
              if (idx < 0 || !points[idx]) return null;
              return (
                <g key={`m-${i}`}>
                  <circle cx={points[idx].x} cy={points[idx].y} r="7" fill="none" stroke="#F59E0B" strokeWidth="2" />
                  <text x={points[idx].x} y={points[idx].y - 12} textAnchor="middle" fontSize="9" fill="#F59E0B">
                    🏆 {m.name || ''}
                  </text>
                </g>
              );
            })}
          </svg>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReadinessTrendChart;
